// RdContextMenu — wiederverwendbares Zeilen-Kontextmenü (Rechtsklick).
//
// Rendert per Portal an `document.body`, damit kein Scroll-Container es
// abschneidet, und nutzt die in contactRedesign.css BEWUSST ungescopten
// Klassen `.rd-ctxmenu` / `.rd-ctx-item` / `.rd-ctx-ic` / `.rd-ctx-sep`.
//
// WICHTIG (Falle aus mail/MailContextMenu.tsx): außerhalb des FluentProviders
// lösen `tokens.*` LEER auf. Deshalb hier keine Fluent-Tokens — die Optik kommt
// vollständig aus dem Stylesheet, das seine Farben hart als Hex führt.
//
// Das Menü positioniert sich am Mauszeiger und wird am Viewport-Rand
// zurückgeklemmt (unten/rechts kippt es nach oben/links), damit ein Rechtsklick
// in der letzten Zeile nicht ins Nichts zeigt.

import * as React from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import { alleFenster, aktivesFenster } from "../../windowManager/browserFenster/fensterRegister";

export type RdMenuItem =
  | { kind: "sep" }
  | {
      kind?: "item";
      /** Stabiler Key. */
      id: string;
      label: string;
      /** Kurzes Zeichen/Glyph links (wie im Spalten-Kontextmenü der DataTable). */
      icon?: React.ReactNode;
      disabled?: boolean;
      /**
       * Hebt den Eintrag rot hervor — für Aktionen, die Daten vernichten.
       * Gleiches Muster wie `gefahr` im Vorgangs-Kontextmenü
       * (`vorgang/v3/Vg3Kontextmenue.tsx`), damit „Löschen" überall gleich
       * aussieht.
       */
      gefahr?: boolean;
      onSelect: () => void;
    };

export type RdContextMenuPosition = { x: number; y: number };

const ITEM_H = 34;
const PAD = 12;
const MIN_W = 210;

/**
 * Klemmt die Menü-Position so in den Viewport, dass das Menü vollständig
 * sichtbar bleibt. Die Höhe wird aus der Item-Zahl geschätzt und nach dem
 * ersten Layout am echten Element korrigiert.
 */
const clampToViewport = (
  pos: RdContextMenuPosition,
  width: number,
  height: number,
): RdContextMenuPosition => ({
  x: Math.max(8, Math.min(pos.x, aktivesFenster().innerWidth - width - 8)),
  y: Math.max(8, Math.min(pos.y, aktivesFenster().innerHeight - height - 8)),
});

export const RdContextMenu: React.FC<{
  position: RdContextMenuPosition | null;
  items: RdMenuItem[];
  onClose: () => void;
  /** Optionale Überschrift (z. B. der Dateiname, auf den geklickt wurde). */
  title?: string;
  /**
   * Das Element, das das Menü geöffnet hat — nötig, wenn es an einem KNOPF
   * hängt statt am Rechtsklick (Telefonie: „Vorgang ▾").
   *
   * Ohne ihn ließe sich ein solches Menü nicht wieder zuklappen: Der
   * Außenklick-Handler unten schließt bei `pointerdown`, und der `click` des
   * Knopfes öffnet unmittelbar danach erneut — der zweite Klick sähe aus, als
   * täte er nichts. Liegt der Zeiger im Anker, schließt das Menü hier also
   * nicht; das Umschalten macht der Knopf selbst.
   */
  ankerRef?: React.RefObject<HTMLElement | null>;
}> = ({ position, items, onClose, title, ankerRef }) => {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = React.useState<RdContextMenuPosition | null>(null);

  // Grobe Vorab-Position (verhindert ein sichtbares Springen), danach exakt
  // am gemessenen Element nachziehen.
  React.useLayoutEffect(() => {
    if (!position) {
      setPos(null);
      return;
    }
    const est = items.length * ITEM_H + PAD * 2 + (title ? 26 : 0);
    setPos(clampToViewport(position, MIN_W, est));
  }, [position, items.length, title]);

  React.useLayoutEffect(() => {
    if (!position || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setPos(clampToViewport(position, r.width, r.height));
  }, [position]);

  React.useEffect(() => {
    if (!position) return;
    // Schließen über `mousedown`/`pointerdown`, NICHT über `click` + `contextmenu`
    // (Muster aus mail/MailContextMenu.tsx). Grund: React delegiert Events an den
    // Root-Container UNTERHALB von window. Ein zweiter Rechtsklick liefe damit
    // erst durch den Zeilen-Handler (setzt die neue Position) und danach durch
    // einen window-`contextmenu`-Listener, der sie sofort wieder auf null setzt —
    // das Menü verschwände, statt sich zu versetzen.
    const onDown = (e: Event) => {
      if (ref.current?.contains(e.target as Node)) return;
      if (ankerRef?.current?.contains(e.target as Node)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    // `true` beim Scroll: der Explorer scrollt in einem inneren Container,
    // dessen Scroll-Events nicht bis window blubbern. Ein Scroll IM Menü
    // schließt es nicht.
    const onScroll = (e: Event) => {
      if (ref.current?.contains(e.target as Node)) return;
      onClose();
    };
    const onResize = () => onClose();
    alleFenster.addEventListener("pointerdown", onDown);
    alleFenster.addEventListener("keydown", onKey);
    alleFenster.addEventListener("scroll", onScroll, true);
    alleFenster.addEventListener("resize", onResize);
    return () => {
      alleFenster.removeEventListener("pointerdown", onDown);
      alleFenster.removeEventListener("keydown", onKey);
      alleFenster.removeEventListener("scroll", onScroll, true);
      alleFenster.removeEventListener("resize", onResize);
    };
  }, [position, onClose, ankerRef]);

  if (!position || !pos) return null;

  return createPortal(
    <div
      ref={ref}
      className="rd-ctxmenu"
      style={{ position: "fixed", top: pos.y, left: pos.x, zIndex: 2100 }}
      role="menu"
      // Klicks im Menü dürfen den globalen Außenklick-Handler nicht auslösen;
      // der jeweilige Eintrag schließt selbst.
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {title ? <div className="rd-ctx-title">{title}</div> : null}
      {items.map((it, i) =>
        it.kind === "sep" ? (
          <div key={`sep-${i}`} className="rd-ctx-sep" />
        ) : (
          <button
            key={it.id}
            type="button"
            role="menuitem"
            className={`rd-ctx-item${it.gefahr ? " gefahr" : ""}`}
            disabled={it.disabled}
            onClick={() => {
              it.onSelect();
              onClose();
            }}
          >
            <span className="rd-ctx-ic">{it.icon ?? ""}</span>
            {it.label}
          </button>
        ),
      )}
    </div>,
    document.body,
  );
};
